/**
 * Enqueue returns a simple helper putting any received callback and
 * arguments  for this callback into a queue, so you can be sure
 * your callbacks will run in sequence and never in parallel.
 *
 * Example #1
 *
 *   // raceConditionsFreeUpdate.js
 *   import Enqueue from 'gleam-shared/promises/enqueue`
 *
 *   const enqueue = Enqueue()
 *
 *   export default function enqueuingUpdate(...args) {
 *     return enqueue(update, ...args)
 *   }
 *
 *   async function update(data) {
 *     await updateOnePart(data.oneChunk)
 *     await updateAnotherPart(data.anotherChunk)
 *   }
 *
 * EnqueuingMethods wraps the received method into a proxy
 * putting the original method into a queue whenever called.
 *
 * Example #2
 *
 *   // prettyRaceConditionFreeUpdate.js
 *   import { EnqueuingMethod } from 'gleam-shared/promises/enqueue`
 *
 *   export default EnqueuingMethod(update)
 *
 *   async function update(data) {
 *     await updateOnePart(data.oneChunk)
 *     await updateAnotherPart(data.anotherChunk)
 *   }
 *
 * EnqueuingMethods wraps the received methods into proxies
 * putting the original methods into a queue whenever called.
 *
 * Example #3
 *
 *   // prettyRaceConditionFreeUpdates.js
 *   import { EnqueuingMethods } from 'gleam-shared/promises/enqueue`
 *
 *   export default EnqueuingMethods({
 *     updateThis,
 *     updateThat
 *   })
 */

import { noop, transform, isFunction } from 'gleam-shared/helpers/nodash'

export default function Enqueue(size = 1) {
  const queues = []

  if (!(size >= 1)) {
    throw new Error('size below 1 does not make sense!')
  }

  for (let index = 0; index !== size; index++) {
    queues.push(Promise.resolve())
  }

  return enqueue

  function enqueue(action, ...args) {
    const last = queues.shift()
    const user = args.length !== 0
     ? last.then(() => action(...args))
     : last.then(action)

    const next = user.catch(noop)
    queues.push(next)

    return user
  }
}

export function EnqueuingMethod(method) {
  if (!isFunction(method)) {
    throw new Error('EnqueuingMethod: "method" is expected to be a function!')
  }

  const enqueue = Enqueue()
  return function enqueuingMethod(...args) {
    return enqueue(method, ...args)
  }
}

export function EnqueuingMethods(methods) {
  const enqueue = Enqueue()
  return transform(methods, (enqueuingMethods, method, methodName) => {
    if (!isFunction(method)) {
      throw new Error(`EnqueuingMethods: ${methodName} is expected to be a function!`)
    }

    enqueuingMethods[methodName] = function enqueuingMethod(...args) {
      return enqueue(method, ...args)
    }
  })
}
